import React, { useState } from 'react';
import { useToast } from "../../../hooks/use-toast";
import { IcdNotCoveredAxiosInstance } from "../../../Network/IcdNotCoveredAxiosInstance";

interface FileUploadProps {
  fetchItems?: () => Promise<void>;
  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "upload-file-icd-not-covered";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

const qaDataAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    "data-testid": id,
    "data-qa": id,
  };
};

const FileUpload: React.FC<FileUploadProps> = ({
  fetchItems,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      handleUpload(selected); // رفع تلقائي
    }
  };

  const handleUpload = async (selectedFile: File) => {
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const { data } = await IcdNotCoveredAxiosInstance.post('uploadFile', formData);

      const { success, message, errors } = data;

      if (success) {
        toast({
          title: "Success",
          description: message || "File uploaded successfully.",
        });

        if (fetchItems) await fetchItems();

        if (errors?.length) {
          const errorMessage = errors.join('\n');
          toast({
            title: "Errors Detected",
            description: errorMessage,
            variant: "destructive",
          });
        }

        clearFile();
      } else {
        toast({
          title: "Failed",
          description: message || "Failed to upload file",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: (error as any)?.response?.data?.error || "Failed to upload file",
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const clearFile = () => {
    setFile(null);
    const input = document.getElementById('file') as HTMLInputElement;
    if (input) input.value = '';
  };

  return (
    <div
      {...qaAttrs(qaIdPrefix, "wrapper")}
      className="inline-flex items-center justify-center"
      data-selected-file-name={file?.name || ""}
      data-has-file={file ? "true" : "false"}
    >
      <input
        id="file"
        {...qaDataAttrs(qaIdPrefix, "input-file")}
        type="file"
        className="hidden"
        onChange={handleFileChange}
      />

      <label
        {...qaAttrs(qaIdPrefix, "button-upload")}
        htmlFor="file"
        className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 
                   hover:from-blue-700 hover:to-blue-800
                   flex items-center justify-center rounded-full 
                   shadow-md cursor-pointer transition-all duration-200
                   active:scale-95 border border-blue-400/30"
        title="Upload File"
        aria-label="Upload ICD Not Covered file"
      >
        <i
          {...qaAttrs(qaIdPrefix, "icon-upload")}
          className="fa-solid fa-upload text-white text-xl"
        ></i>
      </label>

      <span
        {...qaAttrs(qaIdPrefix, "selected-file-name")}
        data-file-name={file?.name || ""}
        style={{ display: "none" }}
      >
        {file?.name || ""}
      </span>
    </div>
  );
};

export default FileUpload;
